import React, { useCallback, useState, useEffect } from 'react'
import { toast } from 'react-toastify'
import 'react-toastify/dist/ReactToastify.css'

import HoverTooltip from '../../../../../../../components/HoverTooltip'

import { ContractDocument, ContractDocumentsError } from '../../types'

type Props = {
    theme: string
    contractAction: boolean
    contractGuid: string
    isAttachment: boolean
    role: string
    onContractDocuments: (have_contract_documents: boolean) => void
    errors?: ContractDocumentsError
    // onAnyEdit: () => void
}

const formatDate = (isoString: string): string => {
    const date = new Date(isoString)
    const day = date.getDate().toString().padStart(2, '0')
    const month = (date.getMonth() + 1).toString().padStart(2, '0')
    const year = date.getFullYear()

    return `${day}.${month}.${year}`
}

const MAX_FILE_SIZE_MB = 50
const ALLOWED_TYPES = [
    // Документы
    'application/pdf', // .pdf
    'application/msword', // .doc
    'application/vnd.openxmlformats-officedocument.wordprocessingml.document', // .docx
    'application/rtf', // .rtf

    // Таблицы
    'application/vnd.ms-excel', // .xls
    'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet', // .xlsx

    // Изображения
    'image/jpeg', // .jpg, .jpeg
    'image/png', // .png
    'image/tiff', // .tiff

    // Архивы
    'application/zip', // .zip
    'application/x-rar-compressed', // .rar

    // Простой текст
    'text/plain',

    'image/bmp', // .bmp
    'image/gif', // .gif
    'text/csv', // .csv
    'application/vnd.oasis.opendocument.presentation', // .odp
    'application/vnd.oasis.opendocument.spreadsheet', // .ods
    'application/vnd.oasis.opendocument.text', // .odt
    'application/vnd.ms-powerpoint', // .ppt
    'application/vnd.openxmlformats-officedocument.presentationml.presentation', // .pptx
    'application/xml',
    'text/html',
    'application/gzip',
]

const ContractDocumentFileUploaderBlock: React.FC<Props> = (props) => {
    const [uploading, setUploading] = useState(false)
    const [uploadedFiles, setUploadedFiles] = useState<ContractDocument[]>([])
    const [infoTooltip, setInfoTooltip] = useState<{
        x: number
        y: number
    } | null>(null)

    const [
        deleteRowContractDocumentFilePopup,
        setDeleteRowContractDocumentFilePopup,
    ] = useState<boolean>(false)

    const [isDeleting, setDeleting] = useState(false)
    const [guidToDelete, setGuidToDelete] = useState<string>('')

    const isAllowedByExtension = (fileName: string) => {
        const allowedExtensions = [
            '.pdf',
            '.docx',
            '.doc',
            '.rtf',
            '.xls',
            '.xlsx',
            '.jpeg',
            '.jpg',
            '.bmp',
            '.tif',
            '.tiff',
            '.txt',
            '.gif',
            '.csv',
            '.odp',
            '.odf',
            '.ods',
            '.odt',
            '.sxc',
            '.sxw',
            '.zip',
            '.rar',
            '.html',
            '.ppt',
            '.pptx',
            '.dwg',
            '.sig',
            '.sgn',
            '.sign',
            '.gzip',
            '.xml',
            '.htm',
            '.png',
        ]
        return allowedExtensions.some((ext) =>
            fileName.toLowerCase().endsWith(ext)
        )
    }

    const Icons = {
        spinner: (
            <svg
                className="animate-spin h-4 w-4 text-[#181818] dark:text-[#FFFFFF]"
                xmlns="http://www.w3.org/2000/svg"
                fill="none"
                viewBox="0 0 24 24"
            >
                <circle
                    className="opacity-25"
                    cx="12"
                    cy="12"
                    r="10"
                    stroke="currentColor"
                    strokeWidth="4"
                ></circle>
                <path
                    className="opacity-75"
                    fill="currentColor"
                    d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z"
                ></path>
            </svg>
        ),
        success: (
            <svg className="w-4 h-4" viewBox="0 0 24 24">
                <path
                    fill="#00FFAA"
                    d="M14.72,8.79l-4.29,4.3L8.78,11.44a1,1,0,1,0-1.41,1.41l2.35,2.36a1,1,0,0,0,.71.29,1,1,0,0,0,.7-.29l5-5a1,1,0,0,0,0-1.42A1,1,0,0,0,14.72,8.79ZM12,2A10,10,0,1,0,22,12,10,10,0,0,0,12,2Zm0,18a8,8,0,1,1,8-8A8,8,0,0,1,12,20Z"
                ></path>
            </svg>
        ),
        error: (
            <svg className="w-4 h-4" viewBox="0 0 24 24">
                <path
                    fill="#FF5555"
                    d="M15.71,8.29a1,1,0,0,0-1.42,0L12,10.59,9.71,8.29A1,1,0,0,0,8.29,9.71L10.59,12l-2.3,2.29a1,1,0,0,0,0,1.42,1,1,0,0,0,1.42,0L12,13.41l2.29,2.3a1,1,0,0,0,1.42,0,1,1,0,0,0,0-1.42L13.41,12l2.3-2.29A1,1,0,0,0,15.71,8.29Zm3.36-3.36A10,10,0,1,0,4.93,19.07,10,10,0,1,0,19.07,4.93ZM17.66,17.66A8,8,0,1,1,20,12,7.95,7.95,0,0,1,17.66,17.66Z"
                ></path>
            </svg>
        ),
    }

    const fetchUploadedFiles = async () => {
        setUploading(true)
        try {
            const response = await fetch(
                process.env.REACT_APP_API_URL +
                    `/api/backend/v1/contract_attachments?contract_guid=${props.contractGuid}`,
                {
                    method: 'GET',
                    credentials: 'include',
                    headers: {
                        Accept: 'application/json',
                    },
                }
            )

            if (!response.ok) throw new Error('Ошибка загрузки файлов')

            const data = await response.json()

            setUploadedFiles(data)
            setUploading(false)
            // props.onAnyEdit()
        } catch (error) {
            console.error('Ошибка при получении файлов:', error)
            toast.error('Не удалось загрузить файлы')
            setUploading(false)
        }
    }

    const uploadFile = async (file: File) => {
        // Проверка типа
        if (
            !ALLOWED_TYPES.includes(file.type) &&
            !isAllowedByExtension(file.name)
        ) {
            toast.error(`Недопустимый тип файла: ${file.name}`)
            return
        }

        // Проверка размера
        if (file.size > MAX_FILE_SIZE_MB * 1024 * 1024) {
            toast(
                `Слишком большой файл (${file.name})\nМаксимальный объем ${MAX_FILE_SIZE_MB} МБ`,
                {
                    type: 'error',
                    style: { whiteSpace: 'pre-line' },
                }
            )
            return
        }

        const formData = new FormData()
        formData.append('file', file) // поле обязательно должно называться "file"

        const toastId = toast.loading(`Загружается: ${file.name}`, {
            icon: Icons.spinner,
        })

        try {
            setUploading(true)
            const response = await fetch(
                process.env.REACT_APP_API_URL +
                    `/api/backend/v1/contract_attachments?contract_guid=${props.contractGuid}`,
                {
                    method: 'POST',
                    credentials: 'include',
                    body: formData,
                    headers: {
                        Accept: 'application/json',
                        // Не указываем Content-Type — браузер сам проставит multipart boundary!
                    },
                }
            )

            if (!response.ok) throw new Error('Upload failed')

            const result = await response.json()
            setUploadedFiles((prev) => [...prev, result])
            toast.update(toastId, {
                render: `Загружено: ${result.filename}`,
                type: 'success',
                isLoading: false,
                autoClose: 3000,
                icon: Icons.success,
            })
            await fetchUploadedFiles()
        } catch (err) {
            console.error(err)
            toast.update(toastId, {
                render: `Ошибка загрузки: ${file.name}`,
                type: 'error',
                isLoading: false,
                autoClose: 3000,
                icon: Icons.error,
            })
        } finally {
            setUploading(false)
        }
    }

    const handleDrop = useCallback((e: React.DragEvent) => {
        e.preventDefault()
        e.stopPropagation()
        if (e.dataTransfer.files.length > 0) {
            Array.from(e.dataTransfer.files).forEach(uploadFile)
        }
    }, [])

    const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const files = e.target.files
        if (files) {
            Array.from(files).forEach(uploadFile)
        }
    }

    const handleDeleteRowContractDocumentFilePopup = () => {
        setDeleteRowContractDocumentFilePopup(
            !deleteRowContractDocumentFilePopup
        )
    }

    const handleDelete = async () => {
        const toastId = toast.loading('Удаление файла...')
        try {
            const res = await fetch(
                process.env.REACT_APP_API_URL +
                    `/api/backend/v1/contract_attachments/${guidToDelete}?contract_guid=${props.contractGuid}`,
                {
                    method: 'DELETE',
                    credentials: 'include',
                    headers: {
                        Accept: 'application/json',
                    },
                }
            )

            if (!res.ok) throw new Error('Удаление не удалось')

            setUploadedFiles((prev) =>
                prev.filter((file) => file.guid !== guidToDelete)
            )
            toast.update(toastId, {
                render: 'Удалено',
                type: 'info',
                isLoading: false,
                autoClose: 3000,
                icon: Icons.success,
            })
            setGuidToDelete('')
            handleDeleteRowContractDocumentFilePopup()
            // props.onAnyEdit()
        } catch (err) {
            toast.update(toastId, {
                render: 'Ошибка удаления',
                type: 'error',
                isLoading: false,
                autoClose: 3000,
                icon: Icons.error,
            })
            handleDeleteRowContractDocumentFilePopup()
        }
    }

    const handleDownload = (guid: string, contractGuid: string) => {
        const form = document.createElement('form')
        form.method = 'GET'
        form.action =
            process.env.REACT_APP_API_URL +
            `/api/backend/v1/contract_attachments/${guid}`
        form.target = '_blank'

        const input = document.createElement('input')
        input.type = 'hidden'
        input.name = 'contract_guid'
        input.value = contractGuid

        form.appendChild(input)
        document.body.appendChild(form)
        form.submit()
        document.body.removeChild(form)
    }

    useEffect(() => {
        fetchUploadedFiles()
    }, [])

    useEffect(() => {
        if (uploadedFiles.length > 0) {
            props.onContractDocuments(true)
        } else {
            props.onContractDocuments(false)
        }
    }, [uploadedFiles])

    // if (!props.isAttachment) return null

    return (
        <div className="flex flex-col w-full space-y-2">
            {props.contractAction ? (
                ''
            ) : (
                <div
                    onDrop={handleDrop}
                    onDragOver={(e) => e.preventDefault()}
                    className="flex w-full items-center justify-center text-center border-2 border-dashed border-[#8A8A8A] rounded-lg p-6 bg-[#e4e6e8] hover:bg-[#f4f6f9] dark:bg-[#242225] dark:hover:bg-[#2A282B] transition"
                >
                    <div className="flex flex-col relative w-full h-full items-center justify-center space-y-2 text-[#6A6A6A] dark:text-[#8A8A8A]">
                        <label
                            htmlFor="fileInput"
                            className="flex flex-row gap-2 text-[14px] rounded-[20px] w-[170px] h-[45px] cursor-pointer items-center justify-center shadow-md shadow-[#18181830] text-[#181818] dark:text-[#FFFFFF] bg-[#F6F6F6] hover:bg-[#FFFFFF] dark:bg-[#494849] dark:hover:bg-[#656365]"
                        >
                            <svg
                                className="w-4 h-4"
                                xmlns="http://www.w3.org/2000/svg"
                                viewBox="0 0 24 24"
                                id="paperclip"
                            >
                                <path
                                    fill={
                                        props.theme == 'dark'
                                            ? '#FFFFFF'
                                            : '#181818'
                                    }
                                    d="M18.08,12.42,11.9,18.61a4.25,4.25,0,0,1-6-6l8-8a2.57,2.57,0,0,1,3.54,0,2.52,2.52,0,0,1,0,3.54l-6.9,6.89A.75.75,0,1,1,9.42,14l5.13-5.12a1,1,0,0,0-1.42-1.42L8,12.6a2.74,2.74,0,0,0,0,3.89,2.82,2.82,0,0,0,3.89,0l6.89-6.9a4.5,4.5,0,0,0-6.36-6.36l-8,8A6.25,6.25,0,0,0,13.31,20l6.19-6.18a1,1,0,1,0-1.42-1.42Z"
                                ></path>
                            </svg>
                            Выберите файл
                        </label>
                        <span className="text-[12.8px]">
                            или перетащите файл сюда
                        </span>
                        <input
                            id="fileInput"
                            type="file"
                            multiple
                            onChange={handleFileChange}
                            className="hidden"
                        />
                        <div
                            className="absolute -top-6 -right-4"
                            style={{ cursor: 'pointer' }}
                        >
                            <HoverTooltip
                                tooltipContent={
                                    <div className="flex flex-col w-[300px] gap-2 items-center justify-start">
                                        <span className="text-[12.8px] w-full text-left">
                                            Максимально допустимый размер файла:
                                            50 МБ.
                                        </span>
                                        <span className="text-[12.8px] w-full text-left">
                                            Допустимый формат файла: pdf, docx,
                                            doc, rtf, xls, xlsx, jpeg, jpg, bmp,
                                            tif, tiff, txt, gif, csv, odp, odf,
                                            ods, odt, sxc, sxw, zip, rar, html,
                                            ppt, pptx, dwg, sig, sgn, sign,
                                            gzip, xml, htm, png.
                                        </span>
                                    </div>
                                }
                                align="left"
                            >
                                <svg
                                    className="w-5 h-5"
                                    id="info-circle"
                                    data-name="Layer 1"
                                    xmlns="http://www.w3.org/2000/svg"
                                    viewBox="0 0 24 24"
                                >
                                    <path
                                        fill={
                                            props.theme == 'dark'
                                                ? '#FFFFFF'
                                                : '#181818'
                                        }
                                        d="M12,2A10,10,0,1,0,22,12,10.01114,10.01114,0,0,0,12,2Zm0,18a8,8,0,1,1,8-8A8.00917,8.00917,0,0,1,12,20Zm0-8.5a1,1,0,0,0-1,1v3a1,1,0,0,0,2,0v-3A1,1,0,0,0,12,11.5Zm0-4a1.25,1.25,0,1,0,1.25,1.25A1.25,1.25,0,0,0,12,7.5Z"
                                    ></path>
                                </svg>
                            </HoverTooltip>
                        </div>
                    </div>
                </div>
            )}

            <HoverTooltip
                tooltipText={props.errors?.have_documents_items || ''}
            >
                <div
                    className={`flex flex-col w-full ${
                        props.errors?.have_documents_items
                            ? 'border border-[#C15C5C] rounded-md'
                            : ''
                    }`}
                >
                    <div className="flex flex-row gap-1 px-1 py-2 h-10 rounded-t-lg items-center justify-center text-[10px] text-[#6A6A6A] dark:text-[#8A8A8A] bg-[#d2d5d8] dark:bg-[#333033]">
                        <div className="flex flex-1 h-full items-center justify-center text-center"></div>
                        <div className="flex flex-col w-[150px] h-full items-center justify-center text-center">
                            <div className="flex w-full h-fit items-center justify-center text-center">
                                Позиций
                                <br />
                                {uploadedFiles
                                    ? uploadedFiles?.length
                                    : 'Нет данных'}
                            </div>
                        </div>
                        <div className="flex flex-col w-[150px] h-full gap-1 items-center justify-center text-center">
                            <div className="flex w-full h-fit items-center justify-center text-center">
                                Общий размер
                                <br />
                                {uploadedFiles && uploadedFiles.length > 0
                                    ? (() => {
                                          const totalBytes =
                                              uploadedFiles.reduce(
                                                  (acc, doc) =>
                                                      acc +
                                                      (doc.file_size || 0),
                                                  0
                                              )

                                          let displaySize = ''
                                          if (totalBytes < 1024) {
                                              displaySize = `${totalBytes} байт`
                                          } else if (totalBytes < 1024 * 1024) {
                                              displaySize = `${(totalBytes / 1024).toFixed(1)} КБ`
                                          } else {
                                              displaySize = `${(totalBytes / (1024 * 1024)).toFixed(1)} МБ`
                                          }

                                          return displaySize
                                      })()
                                    : '0'}
                            </div>
                        </div>
                        <div className="flex w-6 h-full items-center justify-center text-center"></div>
                    </div>
                    <div className="flex flex-row h-10 gap-1 px-1 py-2 text-[11px] text-[#6A6A6A] dark:text-[#8A8A8A] bg-[#dbdbdb] dark:bg-[#2a282b]">
                        <div className="flex w-10 h-full items-center justify-center text-center">
                            №
                        </div>
                        <div className="flex flex-1 h-full items-center justify-center text-center">
                            Наименование документа
                        </div>
                        <div className="flex flex-col w-[150px] h-full items-center justify-center text-center">
                            Дата изменения
                        </div>
                        <div className="flex flex-col w-[150px] h-full items-center justify-center text-center">
                            Размер
                        </div>
                        <div className="flex w-6 h-full items-center justify-center text-center"></div>
                    </div>
                    <div className="flex flex-col gap-1 px-1 py-1 bg-[#e4e6e8] dark:bg-[#242225]">
                        {uploadedFiles.length > 0 ? (
                            uploadedFiles.map((item, index) => (
                                <div
                                    key={item.guid}
                                    className="flex flex-row w-full items-center rounded px-1 py-1 text-[11px] text-[#181818] dark:text-[#FFFFFF] bg-[#f6f6f6] hover:bg-[#FFFFFF] dark:bg-[#2A282B] dark:hover:bg-[#373538]"
                                >
                                    <div className="flex flex-row w-full h-full py-2">
                                        <div className="flex w-10 items-center justify-center text-[#6A6A6A] dark:text-[#8A8A8A]">
                                            {index + 1}
                                        </div>

                                        <div className="flex flex-1 w-full items-center truncate pr-2 gap-3">
                                            <HoverTooltip
                                                tooltipText={item.filename}
                                            >
                                                <p
                                                    className={`onerow ${item.filepath ? 'text-[#0284c7] hover:text-[#38bdf8] cursor-pointer' : 'text-[#181818] dark:text-[#FFFFFF]'}`}
                                                    onClick={() => {
                                                        if (item.filepath) {
                                                            handleDownload(
                                                                item.guid,
                                                                props.contractGuid
                                                            )
                                                        }
                                                    }}
                                                >
                                                    {item.filename}
                                                </p>
                                            </HoverTooltip>
                                            {item.url &&
                                                (props.role == 'admin' ||
                                                props.role == 'specialist' ? (
                                                    <HoverTooltip
                                                        tooltipText={
                                                            'Ссылка в ЕИС'
                                                        }
                                                    >
                                                        <button
                                                            onClick={() => {
                                                                if (item.url) {
                                                                    window.open(
                                                                        item.url,
                                                                        '_blank'
                                                                    )
                                                                }
                                                            }}
                                                            className="flex w-6 h-6 rounded-full items-center justify-center hover:bg-[#FFFFFF] dark:hover:bg-[#656365]"
                                                        >
                                                            <svg
                                                                className="w-4 h-4"
                                                                xmlns="http://www.w3.org/2000/svg"
                                                                viewBox="0 0 24 24"
                                                                id="server-network"
                                                            >
                                                                <path
                                                                    fill={
                                                                        props.theme ==
                                                                        'dark'
                                                                            ? '#0284c7'
                                                                            : '#0284c7'
                                                                    }
                                                                    d="M8,6A1,1,0,1,0,7,5,1,1,0,0,0,8,6ZM21,19H14.82A3,3,0,0,0,13,17.18V15h4a3,3,0,0,0,3-3V10a3,3,0,0,0-.78-2A3,3,0,0,0,20,6V4a3,3,0,0,0-3-3H7A3,3,0,0,0,4,4V6a3,3,0,0,0,.78,2A3,3,0,0,0,4,10v2a3,3,0,0,0,3,3h4v2.18A3,3,0,0,0,9.18,19H3a1,1,0,0,0,0,2H9.18a3,3,0,0,0,5.64,0H21a1,1,0,0,0,0-2ZM6,4A1,1,0,0,1,7,3H17a1,1,0,0,1,1,1V6a1,1,0,0,1-1,1H7A1,1,0,0,1,6,6Zm1,9a1,1,0,0,1-1-1V10A1,1,0,0,1,7,9H17a1,1,0,0,1,1,1v2a1,1,0,0,1-1,1Zm5,8a1,1,0,1,1,1-1A1,1,0,0,1,12,21ZM8,10a1,1,0,1,0,1,1A1,1,0,0,0,8,10Z"
                                                                ></path>
                                                            </svg>
                                                        </button>
                                                    </HoverTooltip>
                                                ) : (
                                                    <HoverTooltip
                                                        tooltipText={
                                                            'Файл хранится в ЕИС'
                                                        }
                                                    >
                                                        <svg
                                                            className="w-4 h-4"
                                                            xmlns="http://www.w3.org/2000/svg"
                                                            viewBox="0 0 24 24"
                                                            id="server-network"
                                                        >
                                                            <path
                                                                fill={
                                                                    props.theme ==
                                                                    'dark'
                                                                        ? '#0284c7'
                                                                        : '#0284c7'
                                                                }
                                                                d="M8,6A1,1,0,1,0,7,5,1,1,0,0,0,8,6ZM21,19H14.82A3,3,0,0,0,13,17.18V15h4a3,3,0,0,0,3-3V10a3,3,0,0,0-.78-2A3,3,0,0,0,20,6V4a3,3,0,0,0-3-3H7A3,3,0,0,0,4,4V6a3,3,0,0,0,.78,2A3,3,0,0,0,4,10v2a3,3,0,0,0,3,3h4v2.18A3,3,0,0,0,9.18,19H3a1,1,0,0,0,0,2H9.18a3,3,0,0,0,5.64,0H21a1,1,0,0,0,0-2ZM6,4A1,1,0,0,1,7,3H17a1,1,0,0,1,1,1V6a1,1,0,0,1-1,1H7A1,1,0,0,1,6,6Zm1,9a1,1,0,0,1-1-1V10A1,1,0,0,1,7,9H17a1,1,0,0,1,1,1v2a1,1,0,0,1-1,1Zm5,8a1,1,0,1,1,1-1A1,1,0,0,1,12,21ZM8,10a1,1,0,1,0,1,1A1,1,0,0,0,8,10Z"
                                                            ></path>
                                                        </svg>
                                                    </HoverTooltip>
                                                ))}
                                        </div>
                                        <div className="flex w-[150px] text-left items-center justify-center">
                                            {formatDate(item.create_datetime)}
                                        </div>
                                        <div className="flex flex-row w-[150px] text-left items-center justify-center">
                                            {item.file_size
                                                ? (() => {
                                                      let displaySize = ''
                                                      if (
                                                          item.file_size < 1024
                                                      ) {
                                                          displaySize = `${item.file_size} байт`
                                                      } else if (
                                                          item.file_size <
                                                          1024 * 1024
                                                      ) {
                                                          displaySize = `${(item.file_size / 1024).toFixed(1)} КБ`
                                                      } else {
                                                          displaySize = `${(item.file_size / (1024 * 1024)).toFixed(1)} МБ`
                                                      }

                                                      return displaySize
                                                  })()
                                                : '-'}
                                        </div>
                                    </div>
                                    {props.contractAction ? (
                                        <div className="flex w-6 h-6 rounded-full items-center justify-center"></div>
                                    ) : (
                                        <button
                                            onClick={() => {
                                                setGuidToDelete(item.guid)
                                                handleDeleteRowContractDocumentFilePopup()
                                            }}
                                            className="flex w-6 h-6 rounded-full items-center justify-center hover:bg-[#ed9cae] dark:hover:bg-[#701F1F]"
                                        >
                                            <svg
                                                className="w-4 h-4"
                                                xmlns="http://www.w3.org/2000/svg"
                                                viewBox="0 0 24 24"
                                                id="trash-alt"
                                            >
                                                <path
                                                    fill={
                                                        props.theme == 'dark'
                                                            ? '#FFFFFF'
                                                            : '#181818'
                                                    }
                                                    d="M10,18a1,1,0,0,0,1-1V11a1,1,0,0,0-2,0v6A1,1,0,0,0,10,18ZM20,6H16V5a3,3,0,0,0-3-3H11A3,3,0,0,0,8,5V6H4A1,1,0,0,0,4,8H5V19a3,3,0,0,0,3,3h8a3,3,0,0,0,3-3V8h1a1,1,0,0,0,0-2ZM10,5a1,1,0,0,1,1-1h2a1,1,0,0,1,1,1V6H10Zm7,14a1,1,0,0,1-1,1H8a1,1,0,0,1-1-1V8H17Zm-3-1a1,1,0,0,0,1-1V11a1,1,0,0,0-2,0v6A1,1,0,0,0,14,18Z"
                                                ></path>
                                            </svg>
                                        </button>
                                    )}
                                </div>
                            ))
                        ) : (
                            <div className="flex w-full h-full py-1 items-center justify-center text-[12.8px] text-[#181818] dark:text-[#FFFFFF]">
                                Нет данных
                            </div>
                        )}
                    </div>
                    <div className="flex flex-row gap-1 px-1 py-2 h-10 rounded-b-lg items-center justify-center text-[10px] text-[#6A6A6A] dark:text-[#8A8A8A] bg-[#d2d5d8] dark:bg-[#333033]">
                        <div className="flex flex-1 h-full items-center justify-center text-center"></div>
                        <div className="flex flex-col w-[150px] h-full items-center justify-center text-center">
                            <div className="flex w-full h-fit items-center justify-center text-center">
                                Позиций
                                <br />
                                {uploadedFiles
                                    ? uploadedFiles?.length
                                    : 'Нет данных'}
                            </div>
                        </div>
                        <div className="flex flex-col w-[150px] h-full gap-1 items-center justify-center text-center">
                            <div className="flex w-full h-fit items-center justify-center text-center">
                                Общий размер
                                <br />
                                {uploadedFiles && uploadedFiles.length > 0
                                    ? (() => {
                                          const totalBytes =
                                              uploadedFiles.reduce(
                                                  (acc, doc) =>
                                                      acc +
                                                      (doc.file_size || 0),
                                                  0
                                              )

                                          let displaySize = ''
                                          if (totalBytes < 1024) {
                                              displaySize = `${totalBytes} байт`
                                          } else if (totalBytes < 1024 * 1024) {
                                              displaySize = `${(totalBytes / 1024).toFixed(1)} КБ`
                                          } else {
                                              displaySize = `${(totalBytes / (1024 * 1024)).toFixed(1)} МБ`
                                          }

                                          return displaySize
                                      })()
                                    : '0'}
                            </div>
                        </div>
                        <div className="flex w-6 h-full items-center justify-center text-center"></div>
                    </div>
                </div>
            </HoverTooltip>
            {deleteRowContractDocumentFilePopup && (
                <div className="fixed top-0 left-0 w-full h-full bg-[#00000050] flex justify-center items-center z-50">
                    <div
                        className="p-5 rounded-lg bg-[#e5e5e5] dark:bg-[#262427] w-fit max-h-[600px]"
                        onClick={(e) => e.stopPropagation()}
                    >
                        <div className="flex flex-col text-sm text-[#181818] dark:text-[#FFFFFF] items-center justify-center gap-5">
                            <div className="flex flex-col">
                                <label className="flex mb-1 text-sm text-[#181818] dark:text-[#FFFFFF]">
                                    Удалить документ?
                                </label>
                            </div>
                            <div className="flex w-full flex-row items-center justify-end gap-3">
                                <button
                                    className="flex flex-row min-w-[120px] h-6 px-4 rounded-[10px] justify-center items-center gap-2 text-xs shadow-md shadow-[#18181830] text-[#181818] dark:text-[#FFFFFF] bg-[#f6f6f6] dark:bg-[#494849] hover:bg-[#FFFFFF] dark:hover:bg-[#656365]"
                                    onClick={
                                        handleDeleteRowContractDocumentFilePopup
                                    }
                                    disabled={isDeleting ? true : false}
                                >
                                    Отмена
                                </button>
                                <button
                                    className="flex px-4 py-1 min-w-[120px] h-6 rounded-[10px] items-center justify-center text-xs shadow-md shadow-[#18181830] text-[#181818] bg-[#ed9cae] hover:bg-[#ffa7ba] dark:bg-[#701F1F] dark:hover:bg-[#9e2c2c] dark:text-[#FFFFFF]"
                                    onClick={handleDelete}
                                >
                                    {isDeleting ? (
                                        <svg
                                            className="animate-spin h-4 w-4 text-[#181818] dark:text-[#FFFFFF]"
                                            xmlns="http://www.w3.org/2000/svg"
                                            fill="none"
                                            viewBox="0 0 24 24"
                                        >
                                            <circle
                                                className="opacity-25"
                                                cx="12"
                                                cy="12"
                                                r="10"
                                                stroke="currentColor"
                                                strokeWidth="4"
                                            ></circle>
                                            <path
                                                className="opacity-75"
                                                fill="currentColor"
                                                d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z"
                                            ></path>
                                        </svg>
                                    ) : (
                                        'Удалить'
                                    )}
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            )}
        </div>
    )
}

export default ContractDocumentFileUploaderBlock
